import type { ReactNode } from 'react';
import { useRef } from 'react';
import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AppConfig } from '@/config';
import { changeLanguage } from '@/i18n/config';
import ShellFooter from '@/features/shell/ShellFooter';
import { useAppearance } from '@/theme/appearanceContext';
import { setUserLocale, type Locale } from '@/utils/locale';
import styles from './signin.module.css';

const LANGUAGES: { locale: Locale; label: string }[] = [
  // Autonyms: each language is named in itself in both UIs.
  { locale: 'ja-JP', label: '日本語' },
  { locale: 'en-US', label: 'English' },
];

/** 日本語 / English on the signed-out pages (stored in the NEXT_LOCALE cookie). */
export const LanguageSwitch = () => {
  const { t, i18n } = useTranslation();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const current = i18n.language === 'en-US' ? 1 : 0;

  const choose = (index: number) => {
    const { locale } = LANGUAGES[index];
    setUserLocale(locale);
    void changeLanguage(locale);
    refs.current[index]?.focus();
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (
      !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)
    )
      return;
    event.preventDefault();
    choose(current === 0 ? 1 : 0);
  };

  return (
    <div
      role="radiogroup"
      aria-label={t('v2.shell.uiLang')}
      className={styles.segmented}
      onKeyDown={onKeyDown}
    >
      {LANGUAGES.map((language, index) => (
        <button
          key={language.locale}
          ref={el => {
            refs.current[index] = el;
          }}
          type="button"
          role="radio"
          lang={language.locale.slice(0, 2)}
          aria-checked={index === current}
          tabIndex={index === current ? 0 : -1}
          className={styles.segment}
          onClick={() => choose(index)}
        >
          {language.label}
        </button>
      ))}
    </div>
  );
};

/**
 * The signed-out page frame (spec §2): logo, heading, the page's actions,
 * the application release label, the language switch and the company,
 * terms and privacy links.
 */
const AuthCard = ({
  title,
  lead,
  children,
}: {
  title: string;
  lead: string;
  children: ReactNode;
}) => {
  const { t } = useTranslation();
  const { scheme } = useAppearance();
  return (
    <div className={styles.page}>
      <main className={styles.card}>
        <img
          src={
            scheme === 'dark'
              ? '/brand/qaAssistLockupOndark.png'
              : '/brand/qaAssistLockup.png'
          }
          alt={t('v2.shell.product')}
          className={styles.logo}
        />
        <div className={styles.heading}>
          <h1 className={styles.title}>{title}</h1>
          <p className={styles.lead}>{lead}</p>
        </div>
        {children}
        <span className={styles.release} data-testid="app-release">
          {t('v2.signIn.release', { release: AppConfig.release })}
        </span>
      </main>
      <LanguageSwitch />
      <ShellFooter className={styles.footer} />
    </div>
  );
};

export default AuthCard;
